import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldCheck, Tag, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PROMO_STORAGE = "boostup_promo";

export const CheckoutModal = ({ open, onOpenChange, pkg }) => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [promo, setPromo] = useState(() => localStorage.getItem(PROMO_STORAGE) || "");
  const [promoState, setPromoState] = useState({ checking: false, valid: false, discount: 0 });
  const [loading, setLoading] = useState(false);

  // Auto-detect ?ref= from URL on first open
  useEffect(() => {
    if (!open) return;
    const url = new URL(window.location.href);
    const ref = url.searchParams.get("ref");
    if (ref && !promo) {
      setPromo(ref.toUpperCase());
    }
  }, [open, promo]);

  // Debounced promo validation
  useEffect(() => {
    const code = promo.trim().toUpperCase();
    if (!code) {
      setPromoState({ checking: false, valid: false, discount: 0 });
      return;
    }
    setPromoState((s) => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/promo/validate", { code });
        if (r.data.valid) {
          setPromoState({ checking: false, valid: true, discount: r.data.discount_usd || 0 });
          localStorage.setItem(PROMO_STORAGE, code);
        } else {
          setPromoState({ checking: false, valid: false, discount: 0, error: r.data.error });
        }
      } catch {
        setPromoState({ checking: false, valid: false, discount: 0 });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [promo]);

  const submit = async () => {
    if (!pkg) return;
    const product = pkg.product || "followers";
    const isPost = product === "likes" || product === "views";
    let target = username.trim();
    if (!target) {
      toast.error(
        isPost
          ? "Paste a full Instagram post or reel URL"
          : `Enter your ${pkg.platform === "tiktok" ? "TikTok" : "Instagram"} username`
      );
      return;
    }
    if (!isPost) target = target.replace(/^@/, "");
    setLoading(true);
    try {
      const platform = pkg.platform || "instagram";
      const payload = {
        package_id: pkg.id,
        instagram_username: target,
        email: email.trim() || null,
        origin_url: window.location.origin,
        promo_code: promoState.valid ? promo.trim().toUpperCase() : null,
        platform,
        product,
      };
      if (pkg.isCustom || pkg.id === "pkg_custom") {
        payload.followers_count = pkg.followers;
      }
      const res = await api.post("/checkout/session", payload);
      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  const finalPrice = pkg ? Math.max(0.5, pkg.amount - (promoState.valid ? promoState.discount : 0)) : 0;
  const product = pkg?.product || "followers";
  const isBest = pkg?.best_deal === true || pkg?.id === "pkg_50k";
  const isTikTok = pkg?.platform === "tiktok";
  const isPost = product === "likes" || product === "views";
  const productUnit = product === "likes" ? "likes" : product === "views" ? "views" : "followers";
  const platformLabel = isTikTok ? "TikTok" : "Instagram";
  const accentClass = isBest
    ? "text-gradient-orange"
    : isTikTok
    ? "text-gradient-tiktok"
    : "text-gradient-neon";
  const btnClass = isBest
    ? "bg-gradient-orange glow-orange"
    : isTikTok
    ? "bg-gradient-tiktok glow-tiktok"
    : "bg-neon glow-pink";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="checkout-modal"
        className="bg-[#0A0A0A] border-white/10 text-white sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle data-testid="checkout-modal-title" className="font-display text-2xl font-black tracking-tighter">
            {pkg ? pkg.label : "Checkout"}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {pkg && (
              <>
                You&apos;re about to purchase{" "}
                <span className="text-white font-semibold">
                  {pkg.followers.toLocaleString()} {platformLabel} {productUnit}
                </span>
                {promoState.valid ? (
                  <>
                    {" "}for{" "}
                    <span className="text-white/40 line-through">${pkg.amount.toFixed(2)}</span>{" "}
                    <span className={`${accentClass} font-bold`}>${finalPrice.toFixed(2)}</span>
                  </>
                ) : (
                  <>
                    {" "}for{" "}
                    <span className={`${accentClass} font-semibold`}>${pkg.amount.toFixed(2)}</span>
                  </>
                )}
                .
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2 block">
              {isPost
                ? `${platformLabel} ${isTikTok ? "video" : "post / reel"} URL`
                : `${platformLabel} username`}
            </label>
            <div className="relative">
              {!isPost && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">@</span>
              )}
              <Input
                data-testid="checkout-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={
                  isPost
                    ? isTikTok
                      ? "https://www.tiktok.com/@user/video/…"
                      : "https://www.instagram.com/p/…"
                    : "yourhandle"
                }
                className={`bg-transparent border-white/10 text-white ${isPost ? "" : "pl-7"} focus:ring-1 ${
                  isBest
                    ? "focus:border-[#FF5E00] focus:ring-[#FF5E00]"
                    : isTikTok
                    ? "focus:border-[#00F2EA] focus:ring-[#00F2EA]"
                    : "focus:border-[#FF007F] focus:ring-[#FF007F]"
                }`}
              />
            </div>
            {isPost && (
              <p className="mt-2 text-[11px] text-white/40 leading-relaxed">
                {isTikTok ? "Video" : "Post"} must be public. Wrong or private URLs cannot be refunded.
              </p>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2 block">
              Email (optional)
            </label>
            <Input
              data-testid="checkout-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="bg-transparent border-white/10 text-white focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2 block flex items-center gap-2">
              <Tag className="w-3 h-3" /> Promo code (optional)
            </label>
            <div className="relative">
              <Input
                data-testid="checkout-promo-input"
                value={promo}
                onChange={(e) => setPromo(e.target.value.toUpperCase())}
                placeholder="ENTER CODE"
                maxLength={20}
                className={`bg-transparent border-white/10 text-white uppercase font-mono tracking-widest pr-10 focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] ${
                  promoState.valid
                    ? "border-[#FF007F]/50"
                    : promo && !promoState.checking && !promoState.valid
                    ? "border-red-400/40"
                    : ""
                }`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {promoState.checking ? (
                  <Loader2 className="w-4 h-4 animate-spin text-white/40" />
                ) : promoState.valid ? (
                  <Check className="w-4 h-4 text-[#FF007F]" />
                ) : promo ? (
                  <X className="w-4 h-4 text-red-400/70" />
                ) : null}
              </div>
            </div>
            {promoState.valid && (
              <p className="text-xs text-[#FF007F] mt-1.5 font-semibold">
                ✓ ${promoState.discount.toFixed(2)} discount applied
              </p>
            )}
          </div>

          <div className="flex items-start gap-2 text-xs text-white/50 bg-white/[0.02] border border-white/10 rounded-lg p-3">
            <ShieldCheck className="w-4 h-4 text-[#FF007F] shrink-0 mt-0.5" />
            <span>We never ask for your password. Secure checkout powered by Stripe.</span>
          </div>

          <button
            data-testid="checkout-submit-button"
            onClick={submit}
            disabled={loading}
            className={`w-full ${btnClass} text-white font-bold py-3.5 rounded-full hover:scale-[1.02] transition-transform disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : `Pay $${finalPrice.toFixed(2)}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutModal;
